import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  combineLatest,
  first,
  map,
  Observable,
  catchError,
  of,
  shareReplay,
  BehaviorSubject,
} from 'rxjs';
import {
  Booking,
  FilterState,
  GuideDetailInterface,
  Scope,
  ShipInterface,
} from '../../core/models';
import { ShipService } from '../../services/resources/ship.service';
import { GuideService } from '../../services/resources/guide.service';
import { CommonModule } from '@angular/common';
import { ShipcalendarComponent } from '../shipcalendar/shipcalendar.component';
import { ShipcalendarHeaderbarComponent } from '../shipcalendar/shipcalendar-headerbar.component';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { BookingDataService } from '../../services/booking-data/booking-data.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { DashboardFilterService } from '../../services/filter/dashboard-filter.service';
import { ShipcalendarSummaryComponent } from '../shipcalendar-summary/shipcalendar-summary.component';
import { LoadingBarrierService } from '../../services/resources/loading-barrier.service';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { BookingSearchComponent } from './booking-search.component';

@Component({
  selector: 'app-dashboard',
  imports: [
    CommonModule,
    ShipcalendarComponent,
    ShipcalendarHeaderbarComponent,
    FormsModule,
    ShipcalendarSummaryComponent,
    ProgressBarComponent,
    BookingSearchComponent,
  ],
  standalone: true,
  template: `
    <div class="relative mx-auto max-w-7xl pt-6 sm:px-6 lg:px-8 min-h-[60vh]">
      <!-- SINGLE overlay; keep mounted, just fade opacity -->
      <div
        class="fixed inset-0 z-50 grid place-items-center transition-opacity duration-100"
        [class.opacity-0]="bootstrapReady()"
        [class.pointer-events-none]="bootstrapReady()"
        aria-live="polite"
        role="status"
      >
        <div
          *ngIf="!bootstrapReady()"
          class="flex w-full flex-col justify-center items-center text-center text-gray-400 italic px-6"
        >
          <div class="w-full max-w-md">
            <app-progress-bar
              [value]="loadingBarAnimationValue()"
              [durationMs]="animationSpeed"
            />
          </div>
          <span>Loading...</span>
        </div>
      </div>

      <!-- READ async ONCE and render everything inside -->
      <ng-container *ngIf="filteredCombinedData$ | async as data">
        <!-- GROUP fade for everything (summary + filters + calendars) -->
        <div [@groupFade]="fadeTick()">
          <div
            [class.opacity-0]="!bootstrapReady()"
            [class.pointer-events-none]="!bootstrapReady()"
          >
            <!-- Summary FIRST (so it computes), then filters, then calendars -->
            <app-shipcalendar-summary [ships]="data.oldShips">
            </app-shipcalendar-summary>

            <!-- Filter Section -->

            <div class="flex flex-col gap-3 px-4 sm:px-0">
              <!-- Row 1: Dropdowns — stacked on mobile, side-by-side on lg -->
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                <!-- COMPANIES BLOCK START -->

                <div class="relative min-w-0 w-full sm:w-0 sm:flex-[2]">
                  <!-- Trigger Button -->
                  <button
                    type="button"
                    (click)="isCompanyDropdownOpen = !isCompanyDropdownOpen"
                    class="w-full truncate py-2 pr-3 pl-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-columbus-blue-500 focus:border-columbus-blue-500 text-sm text-left flex items-center justify-between"
                  >
                    <span class="truncate">
                      {{ companyButtonLabel }}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="size-4 text-black shrink-0 ml-2 transition-transform"
                      [class.rotate-180]="isCompanyDropdownOpen"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="m19.5 8.25-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </button>

                  <!-- Backdrop (für Click-Outside) -->
                  <div
                    *ngIf="isCompanyDropdownOpen"
                    class="fixed inset-0 z-10"
                    (click)="isCompanyDropdownOpen = false"
                  ></div>

                  <!-- Dropdown Panel -->
                  <div
                    *ngIf="isCompanyDropdownOpen"
                    class="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-auto"
                  >
                    <label
                      *ngFor="let company of uniqueCompanies"
                      class="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        [checked]="selectedCompanies.includes(company)"
                        (change)="toggleCompany(company)"
                        class="rounded border-gray-300 text-columbus-blue-600 focus:ring-columbus-blue-500"
                      />
                      <span class="truncate">{{ company }}</span>
                    </label>
                  </div>
                </div>

                <!-- COMPANIES BLOCK END -->

                <select
                  [(ngModel)]="selectedShip"
                  (ngModelChange)="applyShipFilter()"
                  class="form-select min-w-0 w-full sm:w-0 sm:flex-[2] truncate py-2 pr-8 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-columbus-blue-500 focus:border-columbus-blue-500 text-sm"
                >
                  <option value="">All Ships</option>
                  <option
                    *ngFor="let ship of filteredShips"
                    [value]="ship.shipName"
                  >
                    {{ ship.shipName }}
                  </option>
                </select>

                <div class="w-full sm:w-0 sm:flex-[1]">
                  <app-booking-search
                    (searchBookings)="onSearchBookings($event)"
                  />
                </div>
              </div>

              <!-- Row 2: Toggle + Search + Reset — always one row -->

              <div class="flex items-center justify-end gap-3 ">
                <div class="flex items-center gap-2">
                  @if (!showEmpty) {
                    <span class="text-sm pb-2 text-gray-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="size-6"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    </span>
                  } @else {
                    <span class="text-sm  text-black">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="size-6"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                      </svg>
                    </span>
                  }
                  <button
                    type="button"
                    (click)="toggleHideShips()"
                    [ngClass]="{
                      'bg-columbus-blue-600': showEmpty,
                      'bg-gray-200': !showEmpty,
                    }"
                    class="bg-gray-200 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-columbus-blue-600 focus:ring-offset-2"
                    role="switch"
                  >
                    <span
                      aria-hidden="true"
                      [ngClass]="{
                        'translate-x-5': showEmpty,
                        'translate-x-0': !showEmpty,
                      }"
                      class="translate-x-0 pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    ></span>
                  </button>
                </div>
                <button
                  [disabled]="isResetDisabled"
                  (click)="resetFilters()"
                  class="disabled:opacity-25  bg-columbus-blue-600 text-white px-4 py-2 rounded-md hover:bg-columbus-blue-700 focus:outline-none focus:ring-2 focus:ring-columbus-blue-500"
                >
                  <span class="sm:hidden">Reset</span>
                  <span class="hidden sm:inline">Reset Filters</span>
                </button>
              </div>
            </div>
            <!-- Filter Seciton End -->

            <div class="relative">
              <app-shipcalendar-headerbar
                [shipCalendars]="shipCalendars"
                (headerDate)="setCalendarDate($event)"
              >
              </app-shipcalendar-headerbar>

              <ul class="space-y-8">
                <li *ngFor="let ship of data.ships; trackBy: trackByShipId">
                  <app-shipcalendar [ship]="ship"></app-shipcalendar>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-template #errorTemplate>
        <div class="text-red-500 text-center mt-10">
          ⚠️ An error occurred while loading data. Please try again later.
        </div>
      </ng-template>
    </div>
  `,
  styles: ``,
  animations: [
    trigger('groupFade', [
      // first mount
      transition(':enter', [
        style({ opacity: 0 }),
        animate('100ms  ease-out', style({ opacity: 1 })),
      ]),
      // replay on every increment of the bound value
      transition(':increment', [
        style({ opacity: 0 }),
        animate('100ms  ease-out', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChildren(ShipcalendarComponent)
  shipCalendars!: QueryList<ShipcalendarComponent>;

  @ViewChild(BookingSearchComponent)
  bookingSearch!: BookingSearchComponent;
  animationState = 'visible';

  route = inject(ActivatedRoute);
  bookingDataService = inject(BookingDataService);
  filterService = inject(DashboardFilterService);
  // Filter properties
  selectedCompanies: string[] = [];
  selectedShip: string = '';
  searchedBookings: Booking[] = [];
  searchTerm: string = '';

  uniqueCompanies: string[] = [];
  filteredShips: ShipInterface[] = [];

  combinedData$!: Observable<{
    ships: ShipInterface[];
    guides: GuideDetailInterface[];
  }>;

  filteredCombinedData$!: Observable<{
    ships: ShipInterface[];
    guides: GuideDetailInterface[];
    oldShips: ShipInterface[];
  }>;

  calendarDate = new Date();
  hasError = false;

  private filterState$ = new BehaviorSubject<FilterState>({
    selectedCompanies: [],
    selectedShip: '',
    savedDate: null,
    loadedBookings: [],
    searchTerm: '',
    showEmpty: true,
  });

  private readonly barrier = inject(LoadingBarrierService);
  private readonly scope: Scope = 'bootstrap';
  bootstrapReady = this.barrier.isScopeReady(this.scope);
  protected readonly fadeTick = signal(0);
  protected readonly progressBusy = this.barrier.isScopeBusy(this.scope);
  protected readonly progressTarget = this.barrier.progressForScope(this.scope);

  protected animationSpeed = 1000;

  protected showEmpty = true;

  trackByShipId = (_: number, s: ShipInterface) => s.id;

  isCompanyDropdownOpen = false;

  constructor(
    private shipService: ShipService,
    private guideService: GuideService,
  ) {
    effect(() => {
      if (this.bootstrapReady()) {
        // microtask makes sure the DOM is ready before starting the fade
        queueMicrotask(() => this.fadeTick.update((n: number) => n + 1));
      }
    });
  }
  ngOnDestroy(): void {
    this.barrier.resetScope(this.scope);
  }

  loadingBarAnimationValue() {
    if (!this.progressBusy()) return 95;

    this.animationSpeed = 380;
    return 100;
  }

  ngOnInit(): void {
    this.barrier.resetScope(this.scope);
    this.combinedData$ = combineLatest([
      this.shipService.getShips().pipe(
        catchError((error) => {
          console.error('Error loading ships:', error);
          this.hasError = true;
          return of([]); // fallback to empty array
        }),
      ),
      this.guideService.getGuides().pipe(
        catchError((error) => {
          console.error('Error loading guides:', error);
          this.hasError = true;
          return of([]); // fallback to empty array
        }),
      ),
    ]).pipe(
      map(([ships, guides]) => {
        const sortedShips = this.sortShips([...ships]);
        return { ships: sortedShips, guides };
      }),
      shareReplay(1),
    );

    // Setup filtering
    this.setupFiltering();
    this.bookingDataService.clearBooking();
    this.filterService.filters$.pipe(first()).subscribe((fs: FilterState) => {
      if (fs) {
        this.selectedShip = fs.selectedShip;
        this.selectedCompanies = fs.selectedCompanies;
        this.searchedBookings = fs.loadedBookings;
        this.searchTerm = fs.searchTerm;
        this.showEmpty = fs.showEmpty;
        const currentState = this.filterState$.value;
        this.filterState$.next({
          ...currentState,
          selectedCompanies: this.selectedCompanies,
          selectedShip: this.selectedShip,
          loadedBookings: this.searchedBookings,
          searchTerm: this.searchTerm,
          showEmpty: this.showEmpty,
        });
      }
      // console.log('fs', fs);
    });
  }

  get isResetDisabled(): boolean {
    return (
      this.selectedCompanies.length === 0 &&
      this.selectedShip === '' &&
      this.searchTerm === ''
    );
  }

  setCalendarDate(date: Date) {
    this.calendarDate = date;
  }

  private naturalSort(a: string, b: string): number {
    return a.localeCompare(b, undefined, {
      numeric: true, // Enable numeric comparison
      sensitivity: 'base', // Case-insensitive comparison
    });
  }

  // Sorting method to sort ships by company name, then by ship name
  sortShips(ships: ShipInterface[]): ShipInterface[] {
    return ships.sort((a, b) => {
      // First, compare by company name
      const companyComparison = this.naturalSort(a.companyName, b.companyName);

      // If company names are the same, compare by ship name
      if (companyComparison === 0) {
        return this.naturalSort(a.shipName, b.shipName);
      }

      return companyComparison;
    });
  }

  triggerAnimation() {
    this.animationState = 'void'; // force exit
    setTimeout(() => {
      this.animationState = 'visible'; // re-enter
    }, 0);
  }

  setupFiltering() {
    this.filteredCombinedData$ = combineLatest([
      this.combinedData$,
      this.filterState$,
    ]).pipe(
      map(([data, filters]) => {
        const { selectedCompanies, selectedShip, loadedBookings, searchTerm } =
          filters;

        const sortedShips = [...data.ships].sort((a, b) => {
          const companyCompare = this.naturalSort(a.companyName, b.companyName);
          return companyCompare !== 0
            ? companyCompare
            : this.naturalSort(a.shipName, b.shipName);
        });

        this.uniqueCompanies = [
          ...new Set(data.ships.map((s) => s.companyName)),
        ];

        // Ship-Dropdown: gefiltert nach Companies (wenn welche gewählt)
        this.filteredShips =
          selectedCompanies.length > 0
            ? sortedShips.filter((s) =>
                selectedCompanies.includes(s.companyName),
              )
            : sortedShips;
        // Wenn ein Ship gewählt ist das nicht in den Companies ist, trotzdem
        // im Dropdown anzeigen (damit der select-Header nicht leer ist)
        if (
          selectedShip &&
          !this.filteredShips.some((s) => s.shipName === selectedShip)
        ) {
          const orphanShip = sortedShips.find(
            (s) => s.shipName === selectedShip,
          );
          if (orphanShip) {
            this.filteredShips = [orphanShip, ...this.filteredShips];
          }
        }

        // Calendar-Anzeige: Ship hat Priority, sonst Companies, sonst alle
        let displayShips: ShipInterface[];
        if (selectedShip) {
          displayShips = sortedShips.filter((s) => s.shipName === selectedShip);
        } else if (selectedCompanies.length > 0) {
          displayShips = sortedShips.filter((s) =>
            selectedCompanies.includes(s.companyName),
          );
        } else {
          displayShips = sortedShips;
        }

        //Company Fil
        if (loadedBookings.length > 0 || searchTerm !== '') {
          const shipIds = this.findShipsInBookings(loadedBookings);
          displayShips = displayShips.filter((ship) =>
            shipIds.includes(ship.id),
          );
        }

        return {
          ships: displayShips,
          guides: data.guides,
          oldShips: data.ships,
        };
      }),
    );
  }

  private findShipsInBookings(bookings: Booking[]): string[] {
    return Array.from(new Set(bookings.map((booking) => booking.ship.id)));
  }

  applyCompanyFilter(): void {
    this.triggerAnimation();

    const currentState = this.filterState$.value;
    this.filterState$.next({
      ...currentState,
      selectedCompanies: this.selectedCompanies,
      selectedShip: this.selectedShip,
    });
    this.filterService.updateCompanies(this.selectedCompanies);
  }

  applyShipFilter(): void {
    this.triggerAnimation();

    const currentState = this.filterState$.value;
    this.filterState$.next({
      ...currentState,
      selectedShip: this.selectedShip,
    });
    this.filterService.updateShip(this.selectedShip);
  }

  toggleHideShips() {
    this.triggerAnimation();
    const currentState = this.filterState$.value;
    const hideState = !currentState.showEmpty;

    this.showEmpty = hideState;

    this.filterState$.next({
      ...currentState,
      showEmpty: this.showEmpty,
    });

    this.filterService.updateVisibility(this.showEmpty);
  }

  onSearchBookings(data: { bookings: Booking[]; searchTerm: string }) {
    this.triggerAnimation();
    const currentState = this.filterState$.value;
    this.searchedBookings = data.bookings;
    this.searchTerm = data.searchTerm;
    if (data.bookings.length > 0) {
      this.filterState$.next({
        ...currentState,
        selectedCompanies: this.selectedCompanies,
        selectedShip: this.selectedShip,
        loadedBookings: data.bookings,
        searchTerm: data.searchTerm,
      });
      this.filterService.updateloadedBookings(data.bookings);
    } else {
      this.filterState$.next({
        ...currentState,
        selectedCompanies: this.selectedCompanies,
        selectedShip: this.selectedShip,
        loadedBookings: [],
        searchTerm: data.searchTerm,
      });
      this.filterService.updateloadedBookings([]);
    }
  }

  resetFilters(): void {
    this.triggerAnimation();
    this.animationState = 'void';
    setTimeout(() => {
      // Reset selected company and ship
      this.selectedCompanies = [];
      this.selectedShip = '';
      this.searchedBookings = [];
      this.searchTerm = '';
      this.showEmpty = true;
      this.bookingSearch.resetSearch();

      const currentState = this.filterState$.value;
      this.filterState$.next({
        ...currentState,
        selectedCompanies: this.selectedCompanies,
        selectedShip: this.selectedShip,
        loadedBookings: this.searchedBookings,
        searchTerm: this.searchTerm,
        showEmpty: this.showEmpty,
      });
      this.filterService.resetFilters();
      this.animationState = 'visible';
    }, 50);
    // Reapply filtering to show all ships and companies
  }

  get companyButtonLabel(): string {
    if (this.selectedCompanies.length === 0) return 'All Companies';
    if (this.selectedCompanies.length === 1) return this.selectedCompanies[0];
    if (this.selectedCompanies.length === 2)
      return this.selectedCompanies.join(', ');
    return `${this.selectedCompanies.length} Companies`;
  }

  toggleCompany(company: string): void {
    if (this.selectedCompanies.includes(company)) {
      this.selectedCompanies = this.selectedCompanies.filter(
        (c) => c !== company,
      );
    } else {
      this.selectedCompanies = [...this.selectedCompanies, company];
    }
    this.applyCompanyFilter();
  }
}
